import { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff, Link2, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Checkbox, Select } from '../../ui/Controls'
import { Input } from '../../ui/Field'
import { looksSecret } from '../../core/envFile'
import type { GuidedEnvironment } from '../../core/guided'
import {
  DEFAULT_S3_REGION,
  applyS3Connection,
  defaultAwsCopies,
  removeS3Connection,
  s3ConnectionEnv,
  s3ConnectionErrors,
  s3EnvConflicts,
  s3ValuesFromEnv,
  type S3ConnectionField,
  type S3ConnectionRecord,
  type S3ConnectionSource,
  type S3ConnectionValues,
  type S3EnvMapping,
  type S3InstanceCandidate,
} from '../../core/s3Connection'

/** The tenant's running S3 instances, as the route could read them. An error is never an empty list. */
export type S3InstancesState =
  | { status: 'unavailable'; reason: string }
  | { status: 'loading' }
  | { status: 'error'; message: string; retry?: (() => void) | undefined }
  | { status: 'ready'; instances: S3InstanceCandidate[] }

/** An image's own S3 variables, and the name the section shows for it. */
export interface S3Mapping {
  name: string
  env: S3EnvMapping
}

/**
 * The sources a connection can come from, in the order they are offered.
 *
 * This is the extension point: a new source (Phase 2's "DDWS bucket") adds a
 * member to `S3ConnectionSource`, a label here, a case in `resolveSource`
 * that turns its form fields into `S3ConnectionValues`, and a panel in
 * `SourcePanel`. The `Record` type makes the compiler list every place.
 */
export const S3_SOURCE_LABELS: Record<S3ConnectionSource, string> = {
  instance: 'One of my S3 instances',
  external: 'Another S3-compatible endpoint',
}

export const ROOT_KEY_WARNING = 'This gives the service full access to this S3 instance.'
const HOSTNAME_NOTE = 'The address is not looked up here. If it resolves to a private address, the instance cannot reach it.'
const EXPOSURE_NOTE = "The keys are written into this instance's environment. The host can read them in plain text."

type KeyChoice = 'typed' | 'root' | `extra:${number}`

export interface S3FormState {
  source: S3ConnectionSource
  dseq: string
  endpoint: string
  region: string
  bucket: string
  keyChoice: KeyChoice
  accessKeyId: string
  secretAccessKey: string
  awsCopies: boolean
}

function candidateFor(instances: S3InstancesState, dseq: string): S3InstanceCandidate | undefined {
  return instances.status === 'ready' ? instances.instances.find((item) => item.dseq === dseq) : undefined
}

/** The key an instance offers first: a per-app key for the bucket, any per-app key, then the typed fields. */
export function preferredKeyChoice(candidate: S3InstanceCandidate | undefined, bucket: string): KeyChoice {
  const extra = candidate?.credentials?.extra ?? []
  const forBucket = extra.findIndex((key) => key.buckets.includes(bucket.trim()))
  if (forBucket >= 0) return `extra:${forBucket}`
  if (extra.length > 0) return 'extra:0'
  return 'typed'
}

/**
 * Turn the form into connection values, whatever its source. `errors` names
 * the fields to fix; `rootKeys` is whether the values carry an S3 instance's
 * root keys, which the section warns about.
 */
export function resolveSource(form: S3FormState, instances: S3InstancesState): { values: S3ConnectionValues; errors: Partial<Record<S3ConnectionField | 'dseq', string>>; rootKeys: boolean } {
  const typed = { accessKeyId: form.accessKeyId, secretAccessKey: form.secretAccessKey }
  switch (form.source) {
    case 'external': {
      const values = { endpoint: form.endpoint, region: form.region, bucket: form.bucket, ...typed }
      return { values, errors: s3ConnectionErrors(values), rootKeys: false }
    }
    case 'instance': {
      const candidate = candidateFor(instances, form.dseq)
      const credentials = candidate?.credentials
      let keys = typed
      let rootKeys = false
      let keyError: string | undefined
      if (form.keyChoice === 'root' && credentials?.root) {
        keys = credentials.root
        rootKeys = true
      } else if (form.keyChoice.startsWith('extra:')) {
        const extra = credentials?.extra[Number(form.keyChoice.slice('extra:'.length))]
        if (extra) {
          keys = { accessKeyId: extra.accessKeyId, secretAccessKey: extra.secretAccessKey }
          if (form.bucket.trim() && !extra.buckets.includes(form.bucket.trim())) keyError = `This key is limited to ${extra.buckets.join(', ')}.`
        }
      }
      const values = { endpoint: form.endpoint, region: form.region, bucket: form.bucket, ...keys }
      const errors: Partial<Record<S3ConnectionField | 'dseq', string>> = s3ConnectionErrors(values)
      if (!candidate) errors.dseq = 'Choose one of your S3 instances.'
      if (form.endpoint.trim() === '') errors.endpoint = 'Look up the endpoint, or type it.'
      if (keyError && !errors.bucket) errors.bucket = keyError
      // A key the tenant did not type has no field of its own to point at.
      if (form.keyChoice !== 'typed') {
        delete errors.accessKeyId
        delete errors.secretAccessKey
      }
      return { values, errors, rootKeys }
    }
  }
}

function initialForm(env: GuidedEnvironment[], record: S3ConnectionRecord | undefined, sources: readonly S3ConnectionSource[], instances: S3InstancesState): S3FormState {
  const values = record ? s3ValuesFromEnv(env) : { endpoint: '', region: '', bucket: '', accessKeyId: '', secretAccessKey: '' }
  const source = record && sources.includes(record.source) ? record.source : sources[0]!
  const dseq = record?.dseq ?? (instances.status === 'ready' ? instances.instances[0]?.dseq ?? '' : '')
  // Editing: the values are in the environment, so the typed fields hold them.
  const keyChoice: KeyChoice = record ? 'typed' : preferredKeyChoice(candidateFor(instances, dseq), values.bucket)
  return { source, dseq, ...values, region: values.region || (record ? '' : DEFAULT_S3_REGION), keyChoice, awsCopies: defaultAwsCopies(env, record) }
}

/** The editing form, shared by Configure and the SDL editor's "Insert S3 connection". */
export function S3ConnectionForm({
  idPrefix,
  env,
  record,
  sources,
  instances,
  mapping,
  resolveEndpoint,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  idPrefix: string
  env: GuidedEnvironment[]
  record: S3ConnectionRecord | undefined
  sources: readonly S3ConnectionSource[]
  instances: S3InstancesState
  mapping?: S3Mapping | undefined
  resolveEndpoint?: ((candidate: S3InstanceCandidate) => Promise<string | null>) | undefined
  submitLabel: string
  onSubmit: (next: GuidedEnvironment[], record: Omit<S3ConnectionRecord, 'keys'>) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<S3FormState>(() => initialForm(env, record, sources, instances))
  const [submitted, setSubmitted] = useState(false)
  // Until the tenant picks a key, the key follows the bucket (`preferredKeyChoice`).
  // An existing connection's key was chosen already.
  const [keyPicked, setKeyPicked] = useState(Boolean(record))
  const [lookup, setLookup] = useState<{ state: 'idle' | 'busy' } | { state: 'error'; message: string }>({ state: 'idle' })
  const set = (change: Partial<S3FormState>) => setForm((current) => ({ ...current, ...change }))
  const id = (name: string) => `${idPrefix}-${name}`

  // The list can arrive after the form opens: pick its first instance then.
  const firstDseq = instances.status === 'ready' ? instances.instances[0]?.dseq : undefined
  useEffect(() => {
    if (!firstDseq) return
    setForm((current) => current.dseq || current.source !== 'instance' ? current : { ...current, dseq: firstDseq, keyChoice: preferredKeyChoice(candidateFor(instances, firstDseq), current.bucket) })
    // `instances` changes identity with every render of the route; the first dseq is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDseq])

  const resolved = resolveSource(form, instances)
  const next = s3ConnectionEnv(resolved.values, { awsCopies: form.awsCopies, mapping: mapping?.env })
  const conflicts = s3EnvConflicts(env, next, record)
  const candidate = candidateFor(instances, form.dseq)
  const errorFor = (field: S3ConnectionField | 'dseq') => (submitted ? resolved.errors[field] : undefined)

  const chooseInstance = (dseq: string) => {
    const chosen = candidateFor(instances, dseq)
    set({ dseq, endpoint: '', keyChoice: preferredKeyChoice(chosen, form.bucket) })
    // Another instance has other keys: the choice made for the last one does not carry over.
    setKeyPicked(false)
    setLookup({ state: 'idle' })
  }
  const lookUp = async () => {
    if (!candidate || !resolveEndpoint) return
    setLookup({ state: 'busy' })
    try {
      const endpoint = await resolveEndpoint(candidate)
      if (endpoint) {
        set({ endpoint })
        setLookup({ state: 'idle' })
      } else {
        setLookup({ state: 'error', message: 'The host has not reported an address for this instance yet. Check that it is running, or type the endpoint.' })
      }
    } catch (cause) {
      setLookup({ state: 'error', message: `The endpoint could not be read: ${cause instanceof Error ? cause.message : String(cause)}` })
    }
  }
  const submit = () => {
    setSubmitted(true)
    if (Object.keys(resolved.errors).length > 0) return
    onSubmit(next, { source: form.source, dseq: form.source === 'instance' ? form.dseq : undefined, awsCopies: form.awsCopies })
  }

  const keyOptions = [
    ...(candidate?.credentials?.extra ?? []).map((key, index) => ({ value: `extra:${index}`, label: `App key ${key.accessKeyId} (${key.buckets.join(', ')}, ${key.mode === 'rw' ? 'read and write' : 'read only'})` })),
    ...(candidate?.credentials?.root ? [{ value: 'root', label: 'Root keys of this instance' }] : []),
    { value: 'typed', label: 'Type the keys' },
  ]
  const showTypedKeys = form.source !== 'instance' || form.keyChoice === 'typed'

  return <div className="s3c-form" role="group" aria-label="S3 connection">
    {sources.length > 1 && <fieldset className="s3c-sources">
      <legend>Connect to</legend>
      {sources.map((source) => <label key={source} className="s3c-source">
        <input type="radio" name={id('source')} value={source} checked={form.source === source} onChange={() => { set({ source }); setSubmitted(false) }} />
        {S3_SOURCE_LABELS[source]}
      </label>)}
    </fieldset>}

    <SourcePanel form={form} instances={instances} id={id} errorFor={errorFor} onChooseInstance={chooseInstance} lookup={lookup} canLookUp={Boolean(resolveEndpoint && candidate)} onLookUp={() => { void lookUp() }} onChange={set} />

    <div className="s3c-fields">
      <Input id={id('bucket')} label="Bucket" className="row-list-mono" spellCheck={false} autoComplete="off" placeholder="my-bucket" value={form.bucket} error={errorFor('bucket')} onChange={(event) => set({ bucket: event.target.value, ...(form.source === 'instance' && !keyPicked ? { keyChoice: preferredKeyChoice(candidate, event.target.value) } : {}) })} />
      <Input id={id('region')} label="Region" className="row-list-mono" spellCheck={false} autoComplete="off" placeholder={DEFAULT_S3_REGION} value={form.region} error={errorFor('region')} description={`Leave empty for ${DEFAULT_S3_REGION}.`} onChange={(event) => set({ region: event.target.value })} />
    </div>

    {form.source === 'instance' && candidate && <Select id={id('key')} label="Keys" value={form.keyChoice} options={keyOptions} onChange={(event) => { setKeyPicked(true); set({ keyChoice: event.target.value as KeyChoice }) }} {...(candidate.credentials ? {} : { description: 'This browser did not rent this instance, so its keys are not stored here. Type them.' })} />}
    {resolved.rootKeys && <p className="s3c-warning" role="note"><TriangleAlert aria-hidden="true" size={16} />{ROOT_KEY_WARNING}</p>}

    {showTypedKeys && <div className="s3c-fields">
      <Input id={id('access-key')} label="Access key" className="row-list-mono" spellCheck={false} autoComplete="off" value={form.accessKeyId} error={errorFor('accessKeyId')} onChange={(event) => set({ accessKeyId: event.target.value })} />
      <Input id={id('secret-key')} label="Secret key" type="password" className="row-list-mono" spellCheck={false} autoComplete="new-password" value={form.secretAccessKey} error={errorFor('secretAccessKey')} onChange={(event) => set({ secretAccessKey: event.target.value })} />
    </div>}

    <Checkbox id={id('aws')} label="Also set AWS_* variables" description="For AWS SDKs and the AWS CLI, which read them without further setup." checked={form.awsCopies} onChange={(event) => set({ awsCopies: event.target.checked })} />
    {mapping && <p className="field-help">Also sets {Object.keys(mapping.env).length} variable{Object.keys(mapping.env).length === 1 ? '' : 's'} that {mapping.name} reads: {Object.keys(mapping.env).join(', ')}.</p>}
    {conflicts.length > 0 && <p className="s3c-warning" role="note"><TriangleAlert aria-hidden="true" size={16} />Already set, and will be replaced: {conflicts.join(', ')}.</p>}
    <p className="field-help row-list-notice"><Eye aria-hidden="true" size={16} /><span>{EXPOSURE_NOTE}</span></p>

    <div className="row-list-actions">
      <Button id={id('save')} type="button" variant="secondary" size="sm" onClick={submit}>{submitLabel}</Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
    </div>
  </div>
}

function SourcePanel({ form, instances, id, errorFor, onChooseInstance, lookup, canLookUp, onLookUp, onChange }: {
  form: S3FormState
  instances: S3InstancesState
  id: (name: string) => string
  errorFor: (field: S3ConnectionField | 'dseq') => string | undefined
  onChooseInstance: (dseq: string) => void
  lookup: { state: 'idle' | 'busy' } | { state: 'error'; message: string }
  canLookUp: boolean
  onLookUp: () => void
  onChange: (change: Partial<S3FormState>) => void
}) {
  switch (form.source) {
    case 'external':
      return <div className="s3c-fields">
        <Input id={id('endpoint')} label="Endpoint" className="row-list-mono" type="url" inputMode="url" spellCheck={false} autoComplete="off" placeholder="https://s3.example.test" value={form.endpoint} error={errorFor('endpoint')} description={HOSTNAME_NOTE} onChange={(event) => onChange({ endpoint: event.target.value })} />
      </div>
    case 'instance':
      if (instances.status === 'unavailable') return <p className="field-help">{instances.reason}</p>
      if (instances.status === 'loading') return <p className="field-help" role="status">Loading your S3 instances…</p>
      if (instances.status === 'error') {
        return <div className="banner banner-error" role="alert"><div className="banner-body"><strong>Your S3 instances could not be loaded</strong><p>{instances.message}</p>{instances.retry && <Button type="button" variant="secondary" size="sm" onClick={instances.retry}>Try again</Button>}</div></div>
      }
      if (instances.instances.length === 0) return <p className="field-help">You have no running S3 instances. Rent one from the catalog, or connect to another endpoint.</p>
      return <div className="s3c-fields">
        <Select id={id('instance')} label="S3 instance" value={form.dseq} {...(errorFor('dseq') ? { error: errorFor('dseq')! } : {})} options={instances.instances.map((item) => ({ value: item.dseq, label: `${item.name} (#${item.dseq})` }))} onChange={(event) => onChooseInstance(event.target.value)} />
        <div className="s3c-endpoint">
          <Input id={id('endpoint')} label="Endpoint" className="row-list-mono" type="url" inputMode="url" spellCheck={false} autoComplete="off" placeholder="https://…" value={form.endpoint} error={errorFor('endpoint') ?? (lookup.state === 'error' ? lookup.message : undefined)} onChange={(event) => onChange({ endpoint: event.target.value })} />
          {canLookUp && <Button type="button" variant="secondary" size="sm" loading={lookup.state === 'busy'} onClick={onLookUp}><Link2 aria-hidden="true" size={16} />Look up endpoint</Button>}
        </div>
      </div>
  }
}

/**
 * The Configure section. Shows the connection the environment holds, masked,
 * with Edit and Remove; or the form. Editing and removing touch exactly the
 * variables the connection wrote (`record.keys`).
 */
export function S3Connection({ env, record, instances, mapping, resolveEndpoint, onChange }: {
  env: GuidedEnvironment[]
  record: S3ConnectionRecord | undefined
  instances: S3InstancesState
  mapping?: S3Mapping | undefined
  resolveEndpoint?: ((candidate: S3InstanceCandidate) => Promise<string | null>) | undefined
  onChange: (env: GuidedEnvironment[], record: S3ConnectionRecord | undefined) => void
}) {
  const [editing, setEditing] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const rows = useMemo(() => (record ? record.keys.map((key) => env.find((row) => row.key.trim() === key)).filter((row): row is GuidedEnvironment => row !== undefined) : []), [env, record])
  const sources: readonly S3ConnectionSource[] = ['instance', 'external']

  if (editing) {
    return <S3ConnectionForm
      idPrefix="s3c"
      env={env}
      record={record}
      sources={sources}
      instances={instances}
      mapping={mapping}
      resolveEndpoint={resolveEndpoint}
      submitLabel="Save connection"
      onSubmit={(next, meta) => {
        const applied = applyS3Connection(env, record, next, meta)
        onChange(applied.env, applied.record)
        setEditing(false)
        setRevealed(false)
      }}
      onCancel={() => setEditing(false)}
    />
  }

  if (!record) {
    return <>
      <p className="field-help row-list-empty">No S3 connection. Connect this instance to one of your S3 instances or to any S3-compatible endpoint, and the console writes the details into its environment.</p>
      <div className="row-list-actions">
        <Button id="s3c-add" type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}><Plus aria-hidden="true" size={16} />Add S3 connection</Button>
      </div>
    </>
  }

  return <>
    <p className="field-help">{record.source === 'instance' && record.dseq ? `Connected to S3 instance #${record.dseq}.` : 'Connected to an S3-compatible endpoint.'} These variables are set in the environment:</p>
    <dl className="s3c-summary" aria-label="S3 connection variables">
      {rows.map((row) => <div key={row.key}>
        <dt className="row-list-mono">{row.key}</dt>
        <dd className="row-list-mono">{looksSecret(row.key) && !revealed ? '••••••••' : row.value}</dd>
      </div>)}
    </dl>
    <div className="row-list-actions">
      <Button type="button" variant="ghost" size="sm" aria-pressed={revealed} onClick={() => setRevealed((value) => !value)}>{revealed ? <EyeOff aria-hidden="true" size={16} /> : <Eye aria-hidden="true" size={16} />}{revealed ? 'Hide keys' : 'Show keys'}</Button>
      <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}><Pencil aria-hidden="true" size={16} />Edit</Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(removeS3Connection(env, record), undefined); setRevealed(false) }}><Trash2 aria-hidden="true" size={16} />Remove</Button>
    </div>
  </>
}
